// eslint-disable-next-line import/no-extraneous-dependencies
import dagre from '@dagrejs/dagre';
import { type Edge, type Node, Position } from '@xyflow/react';
import { type BaseNodeData } from '../types';
import { hasEstimatedHeight, isSwitchStepNode } from '../types/typeGuards';
import {
	NODE_WIDTH,
	BRANCH_VERTICAL_GAP,
	BEHAVIOR_BRANCH_VERTICAL_GAP,
	INTRA_BRANCH_VERTICAL_GAP,
	BRANCH_MARGIN,
	CONTAINER_PADDING,
	SEGMENT_NODE,
	BEHAVIOR_CASE_NODE,
	STEP_NODE_COMPACT_HEIGHT,
	TRIGGER_NODE,
} from './layoutConstants';

// Vertical layout configuration
const VERTICAL_LAYOUT_CONFIG = {
	direction: 'TB' as const,
	nodeSpacing: 40, // Horizontal spacing between sibling nodes (40px margin)
	rankSpacing: 80, // Vertical spacing between ranks
};

const DEFAULT_NODE_HEIGHT = 120;

// Maximum expected height for a compact (collapsed) node.
// If measured.height exceeds this after a collapse, it is stale from the
// previous expansion and must be ignored to avoid positioning downstream
// nodes as though the expanded card is still present.
const MAX_COMPACT_NODE_HEIGHT = 200;

// Soft caps for nested lane expansion. Caps ensure outer lanes grow enough to
// contain inner branches without unbounded horizontal expansion. The cap is
// generous enough that inner switches with up to 4 branches fit comfortably;
// deeper nesting is progressively dampened to keep total canvas width practical.
const MAX_NESTED_LANE_WIDTH = SEGMENT_NODE.WIDTH * 4 + BRANCH_MARGIN * 3;

function getNodeHeight(node: Node<BaseNodeData>): number {
	const measuredHeight = typeof node.measured?.height === 'number' ? node.measured.height : null;
	const estimatedHeight = hasEstimatedHeight(node.data) ? node.data.estimatedHeight : null;
	const isExpanded = (node.data as Record<string, unknown>)?.isExpanded === true;

	let resolvedHeight: number;

	if (isExpanded) {
		if (measuredHeight !== null && estimatedHeight !== null) {
			resolvedHeight = Math.max(measuredHeight, estimatedHeight);
		} else if (estimatedHeight !== null) {
			resolvedHeight = estimatedHeight;
		} else if (measuredHeight !== null) {
			resolvedHeight = measuredHeight;
		} else if (node.type === 'behaviorCase') {
			resolvedHeight = BEHAVIOR_CASE_NODE.EXPANDED_HEIGHT;
		} else {
			resolvedHeight = -1; // will fall through
		}
	} else {
		if (measuredHeight !== null && measuredHeight <= MAX_COMPACT_NODE_HEIGHT) {
			resolvedHeight = measuredHeight;
		} else if (estimatedHeight !== null && estimatedHeight <= MAX_COMPACT_NODE_HEIGHT) {
			resolvedHeight = estimatedHeight;
		} else if (node.type === 'segment') {
			resolvedHeight = SEGMENT_NODE.COLLAPSED_HEIGHT;
		} else if (node.type === 'behaviorCase') {
			resolvedHeight = BEHAVIOR_CASE_NODE.HEIGHT;
		} else if (node.type !== 'trigger' && node.type !== 'phantomTarget') {
			resolvedHeight = STEP_NODE_COMPACT_HEIGHT;
		} else {
			resolvedHeight = -1; // will fall through
		}
	}

	if (resolvedHeight === -1) {
		if (typeof node.height === 'number') {
			resolvedHeight = node.height;
		} else if (estimatedHeight !== null) {
			resolvedHeight = estimatedHeight;
		} else if (node.type === 'segment') {
			resolvedHeight = SEGMENT_NODE.MIN_HEIGHT;
		} else if (node.type === 'trigger') {
			resolvedHeight = TRIGGER_NODE.HEIGHT;
		} else {
			resolvedHeight = DEFAULT_NODE_HEIGHT;
		}
	}

	return resolvedHeight;
}

/**
 * Gets the visual/stacking height for a node.
 * Used for vertical stacking within branches where we need the actual rendered height.
 * Delegates to getNodeHeight which handles the measured vs estimated height priority.
 */
function getNodeStackingHeight(node: Node<BaseNodeData>): number {
	return getNodeHeight(node);
}

/**
 * Gets the actual width for a node based on its type.
 * All visible nodes use a consistent width for alignment:
 * - Segment nodes: 560px
 * - Behavior case nodes: 560px
 * - Trigger nodes: 560px
 * - Step nodes: 560px
 * - Phantom nodes: 1px (invisible drop targets)
 */
function getNodeWidth(node: Node<BaseNodeData>): number {
	if (node.type === 'segment') {
		return SEGMENT_NODE.WIDTH;
	}
	if (node.type === 'behaviorCase') {
		return BEHAVIOR_CASE_NODE.WIDTH;
	}
	if (node.type === 'trigger') {
		return TRIGGER_NODE.WIDTH;
	}
	if (node.type === 'phantomTarget') {
		return 1;
	}
	return NODE_WIDTH;
}

export interface LayoutPipelineOptions {
	/**
	 * Enables the recursive container graph pass (local -> global -> translate).
	 * Keep this true by default; can be disabled via caller flag for rollback.
	 */
	useContainerLayoutV2?: boolean;
}

export function applyLayout(
	nodes: Node<BaseNodeData>[],
	edges: Edge[],
	options: LayoutPipelineOptions = {}
): { nodes: Node<BaseNodeData>[]; edges: Edge[] } {
	const config = VERTICAL_LAYOUT_CONFIG;
	const dagreGraph = new dagre.graphlib.Graph();
	dagreGraph.setDefaultEdgeLabel(() => ({}));

	dagreGraph.setGraph({
		rankdir: config.direction,
		nodesep: config.nodeSpacing,
		ranksep: config.rankSpacing,
		ranker: 'network-simplex',
	});

	nodes.forEach(node => {
		dagreGraph.setNode(node.id, {
			width: getNodeWidth(node),
			height: getNodeHeight(node),
		});
	});

	edges.forEach(edge => {
		dagreGraph.setEdge(edge.source, edge.target);
	});

	dagre.layout(dagreGraph);

	const layoutedNodesWithPositions: Node<BaseNodeData>[] = nodes.map(node => {
		const nodeWithPosition = dagreGraph.node(node.id);
		const nodeHeight = getNodeHeight(node);
		const nodeWidth = getNodeWidth(node);

		return {
			...node,
			targetPosition: Position.Top,
			sourcePosition: Position.Bottom,
			data: {
				...node.data,
				targetPosition: Position.Top,
				sourcePosition: Position.Bottom,
			},
			position: {
				// Dagre returns center coordinates, convert to top-left
				x: nodeWithPosition.x - nodeWidth / 2,
				y: nodeWithPosition.y - nodeHeight / 2,
			},
		};
	});

	const layoutedNodes = applySwitchBranchLayout(layoutedNodesWithPositions, edges, {
		useContainerLayoutV2: options.useContainerLayoutV2 !== false,
	});

	return { nodes: layoutedNodes, edges };
}

/**
 * Represents metadata about a switch node and its branches.
 * Includes nesting-aware fields for multi-stage layout processing.
 */
interface SwitchNodeMetadata {
	switchNodeId: string;
	branches: Map<string, string[]>; // branchKey -> array of node IDs in that branch
	branchRoots: Map<string, string>; // branchKey -> direct child target ID for that branch
	branchRootTypes: Map<string, 'segment' | 'behaviorCase' | 'other'>;
	visualParentId: string | null; // Visible node where this switch branches from
	parentSwitchId: string | null; // Which switch contains this one (null = top-level)
	parentBranchKey?: string | null;
	parentBranchRootId?: string | null;
	parentBranchRootType?: 'segment' | 'behaviorCase' | 'other' | null;
	nestingDepth: number; // 0 = top-level, higher = deeper nested
	boundingBox?: { width: number; height: number }; // Computed after local layout
}

/**
 * Explicit container model used by the nested layout pipeline.
 * Containers are synthesized around switch nodes so we can run:
 * 1) local child layout, 2) global container graph layout, 3) child translation.
 */
interface LayoutContainer {
	id: string;
	switchNodeId: string | null;
	parentContainerId: string | null;
	childContainerIds: string[];
	depth: number;
	localBounds: { width: number; height: number };
	center: { x: number; y: number };
}

interface LayoutContext {
	rootContainerId: string;
	containers: Map<string, LayoutContainer>;
	switchToContainerId: Map<string, string>;
}

/**
 * Identifies switch nodes and their branches in the flow.
 * Returns metadata about each switch node including which nodes belong to which branch.
 *
 * Since switch nodes are now hidden, branches are identified by checking edge data
 * for the `switchNodeId` property, not by checking the edge source.
 *
 * @param nodes - All nodes in the flow
 * @param edges - All edges in the flow
 * @returns Array of switch node metadata
 */
function identifySwitchBranches(nodes: Node<BaseNodeData>[], edges: Edge[]): SwitchNodeMetadata[] {
	const switchMetadata: SwitchNodeMetadata[] = [];

	const nodeById = new Map<string, Node<BaseNodeData>>();
	for (const node of nodes) {
		nodeById.set(node.id, node);
	}

	const outgoingEdgesBySource = new Map<string, Edge[]>();
	for (const edge of edges) {
		const list = outgoingEdgesBySource.get(edge.source) || [];
		list.push(edge);
		outgoingEdgesBySource.set(edge.source, list);
	}

	// Find all switch nodes (including hidden ones)
	const switchNodes = nodes.filter(node => isSwitchStepNode(node.data));

	for (const switchNode of switchNodes) {
		const branches = new Map<string, string[]>();
		const branchRoots = new Map<string, string>();
		const branchRootTypes = new Map<string, 'segment' | 'behaviorCase' | 'other'>();

		// Find all branch edges belonging to this switch node.
		// Since switch nodes are hidden, edges now originate from the parent step
		// but contain `switchNodeId` in their data to identify the owning switch.
		const branchEdges = edges.filter(edge => {
			if (edge.type !== 'branch') return false;
			const edgeData = edge.data as { switchNodeId?: string } | undefined;
			// Check if edge belongs to this switch (via switchNodeId in data)
			// or if it's an old-style edge directly from the switch node
			return edgeData?.switchNodeId === switchNode.id || edge.source === switchNode.id;
		});

		// Sort by caseIndex from edge data for consistent left-to-right ordering.
		// If caseIndex is missing, use stable original ordering and make branch keys unique
		// so branches are never overwritten in the map.
		const sortedBranchEdges = branchEdges
			.map((edge, originalIndex) => {
				const rawCaseIndex = (edge.data as { caseIndex?: unknown })?.caseIndex;
				const normalizedCaseIndex =
					typeof rawCaseIndex === 'number' && Number.isFinite(rawCaseIndex) ? rawCaseIndex : originalIndex;
				return { edge, originalIndex, normalizedCaseIndex };
			})
			.sort((a, b) => {
				if (a.normalizedCaseIndex !== b.normalizedCaseIndex) {
					return a.normalizedCaseIndex - b.normalizedCaseIndex;
				}
				return a.originalIndex - b.originalIndex;
			});

		// All branches for a switch should share the same visual parent (edge source).
		const visualParentId = sortedBranchEdges[0]?.edge.source ?? null;

		// Process each branch edge
		for (const { edge: branchEdge, originalIndex, normalizedCaseIndex } of sortedBranchEdges) {
			const branchKey = `branch-${normalizedCaseIndex}-${originalIndex}`;
			const branchNodeIds: string[] = [];
			const visited = new Set<string>();
			const queue: string[] = [branchEdge.target];
			visited.add(branchEdge.target);

			// Find all descendants using BFS
			while (queue.length > 0) {
				const currentNodeId = queue.shift()!;
				branchNodeIds.push(currentNodeId);

				// Find children of current node (but stop at other switch nodes)
				const currentNode = nodeById.get(currentNodeId);
				if (!currentNode) {
					continue;
				}

				const isAnotherSwitch = isSwitchStepNode(currentNode.data);

				if (!isAnotherSwitch) {
					const childEdges = outgoingEdgesBySource.get(currentNodeId) || [];
					for (const edge of childEdges) {
						// Never traverse phantom add-path edges when collecting branch descendants.
						if (edge.type === 'phantomAddPath') {
							continue;
						}

						// Stop traversal at nested switch branch boundaries.
						// If a branch edge belongs to another switch, the outer switch should not
						// absorb those descendants into its own lane chain.
						if (edge.type === 'branch') {
							const edgeOwnerSwitchId = (edge.data as { switchNodeId?: string } | undefined)?.switchNodeId;
							if (edgeOwnerSwitchId && edgeOwnerSwitchId !== switchNode.id) {
								continue;
							}
						}

						if (!visited.has(edge.target)) {
							visited.add(edge.target);
							queue.push(edge.target);
						}
					}
				}
			}

			branches.set(branchKey, branchNodeIds);
			branchRoots.set(branchKey, branchEdge.target);
			const branchRootNode = nodeById.get(branchEdge.target);
			branchRootTypes.set(
				branchKey,
				branchRootNode?.type === 'segment'
					? 'segment'
					: branchRootNode?.type === 'behaviorCase'
						? 'behaviorCase'
						: 'other'
			);
		}

		switchMetadata.push({
			switchNodeId: switchNode.id,
			branches,
			branchRoots,
			branchRootTypes,
			visualParentId,
			parentSwitchId: null,
			parentBranchKey: null,
			parentBranchRootId: null,
			parentBranchRootType: null,
			nestingDepth: 0,
		});
	}

	return switchMetadata;
}

/**
 * Populates parentSwitchId and nestingDepth on each SwitchNodeMetadata entry.
 * A switch is nested inside another when its visual parent appears inside one of the
 * outer switch branch chains (or, for backward compatibility, when the switch node id
 * itself already appears in that branch list).
 * Depth is computed by walking the parent chain (top-level = 0, deeper = higher).
 */
function buildSwitchNestingTree(switchMetadata: SwitchNodeMetadata[]): void {
	// Build a lookup: switchNodeId -> its metadata
	const metaById = new Map<string, SwitchNodeMetadata>();
	for (const meta of switchMetadata) {
		metaById.set(meta.switchNodeId, meta);
	}

	// For each switch, determine the most specific parent by looking for its visual
	// parent inside another switch's branch descendants. Branch entry identity matters:
	// behaviorCase and segment roots are both first-class branch boundaries, so we keep
	// the containing branch key/root metadata alongside the chosen parent switch.
	for (const meta of switchMetadata) {
		const candidates: {
			outerMeta: SwitchNodeMetadata;
			branchKey: string;
			branchNodeIds: string[];
			branchRootId: string | null;
			branchRootType: 'segment' | 'behaviorCase' | 'other';
			visualParentIndex: number;
			matchedByVisualParent: boolean;
		}[] = [];

		for (const outerMeta of switchMetadata) {
			if (outerMeta.switchNodeId === meta.switchNodeId) {
				continue;
			}

			for (const [branchKey, branchNodeIds] of outerMeta.branches.entries()) {
				const visualParentIndex = meta.visualParentId ? branchNodeIds.indexOf(meta.visualParentId) : -1;
				const containsHiddenSwitch = branchNodeIds.includes(meta.switchNodeId);
				if (visualParentIndex < 0 && !containsHiddenSwitch) {
					continue;
				}

				candidates.push({
					outerMeta,
					branchKey,
					branchNodeIds,
					branchRootId: outerMeta.branchRoots.get(branchKey) || null,
					branchRootType: outerMeta.branchRootTypes.get(branchKey) || 'other',
					visualParentIndex,
					matchedByVisualParent: visualParentIndex >= 0,
				});
			}
		}

		if (candidates.length === 0) {
			continue;
		}

		const scoreCandidateSpecificity = (candidate: { outerMeta: SwitchNodeMetadata }): number => {
			let score = 0;
			for (const other of candidates) {
				if (other.outerMeta.switchNodeId === candidate.outerMeta.switchNodeId) {
					continue;
				}
				for (const otherBranchNodeIds of other.outerMeta.branches.values()) {
					if (otherBranchNodeIds.includes(candidate.outerMeta.switchNodeId)) {
						score++;
						break;
					}
				}
			}
			return score;
		};

		candidates.sort((a, b) => {
			const specificityDelta = scoreCandidateSpecificity(b) - scoreCandidateSpecificity(a);
			if (specificityDelta !== 0) {
				return specificityDelta;
			}
			if (a.matchedByVisualParent !== b.matchedByVisualParent) {
				return a.matchedByVisualParent ? -1 : 1;
			}
			if (a.branchRootType !== b.branchRootType) {
				if (a.branchRootType === 'behaviorCase' || a.branchRootType === 'segment') {
					return -1;
				}
				if (b.branchRootType === 'behaviorCase' || b.branchRootType === 'segment') {
					return 1;
				}
			}
			return b.visualParentIndex - a.visualParentIndex;
		});

		const bestCandidate = candidates[0];
		meta.parentSwitchId = bestCandidate.outerMeta.switchNodeId;
		meta.parentBranchKey = bestCandidate.branchKey;
		meta.parentBranchRootId = bestCandidate.branchRootId;
		meta.parentBranchRootType = bestCandidate.branchRootType;

		// Ensure the nested hidden switch itself is represented in the chosen parent
		// branch chain so outer lane sizing can account for the nested subtree width.
		if (!bestCandidate.branchNodeIds.includes(meta.switchNodeId)) {
			const updated = [...bestCandidate.branchNodeIds];
			const branchRootIndex = bestCandidate.branchRootId ? updated.indexOf(bestCandidate.branchRootId) : -1;
			const insertionIndex =
				bestCandidate.visualParentIndex >= 0
					? bestCandidate.visualParentIndex + 1
					: branchRootIndex >= 0
						? branchRootIndex + 1
						: updated.length;
			updated.splice(insertionIndex, 0, meta.switchNodeId);
			bestCandidate.outerMeta.branches.set(bestCandidate.branchKey, updated);
		}
	}

	// Calculate depth by walking parent chain.
	for (const meta of switchMetadata) {
		let depth = 0;
		let current: SwitchNodeMetadata | undefined = meta;
		const visited = new Set<string>();

		while (current?.parentSwitchId !== null && current?.parentSwitchId !== undefined) {
			if (visited.has(current.parentSwitchId)) {
				break;
			}
			visited.add(current.parentSwitchId);
			depth++;
			current = metaById.get(current.parentSwitchId);
		}
		meta.nestingDepth = depth;
	}
}

function getSwitchContainerId(switchNodeId: string): string {
	return `container-${switchNodeId}`;
}

function buildLayoutContext(switchMetadata: SwitchNodeMetadata[]): LayoutContext {
	const rootContainerId = 'container-root';
	const containers = new Map<string, LayoutContainer>();
	const switchToContainerId = new Map<string, string>();

	containers.set(rootContainerId, {
		id: rootContainerId,
		switchNodeId: null,
		parentContainerId: null,
		childContainerIds: [],
		depth: 0,
		localBounds: { width: 0, height: 0 },
		center: { x: 0, y: 0 },
	});

	for (const meta of switchMetadata) {
		const containerId = getSwitchContainerId(meta.switchNodeId);
		switchToContainerId.set(meta.switchNodeId, containerId);
		containers.set(containerId, {
			id: containerId,
			switchNodeId: meta.switchNodeId,
			parentContainerId: meta.parentSwitchId ? getSwitchContainerId(meta.parentSwitchId) : rootContainerId,
			childContainerIds: [],
			depth: meta.nestingDepth,
			localBounds: {
				width: meta.boundingBox?.width || SEGMENT_NODE.WIDTH,
				height: meta.boundingBox?.height || STEP_NODE_COMPACT_HEIGHT,
			},
			center: { x: 0, y: 0 },
		});
	}

	for (const container of containers.values()) {
		if (!container.parentContainerId) {
			continue;
		}
		const parent = containers.get(container.parentContainerId);
		if (!parent) {
			continue;
		}
		parent.childContainerIds.push(container.id);
	}

	return {
		rootContainerId,
		containers,
		switchToContainerId,
	};
}

/**
 * Gets the effective width for a branch lane, accounting for nested switch bounding boxes.
 * If a branch contains a nested switch with a known bounding box, the lane must be
 * at least as wide as that bounding box.
 */
function getEffectiveBranchWidth(
	branchNodeIds: string[],
	nodes: Node<BaseNodeData>[],
	switchBoundingBoxes: Map<string, { width: number; height: number }>,
	minLaneWidth: number,
	_maxNestedLaneWidth: number,
	_switchDepthById?: Map<string, number>
): number {
	let maxWidth: number = minLaneWidth;
	for (const nodeId of branchNodeIds) {
		const bbox = switchBoundingBoxes.get(nodeId);
		if (bbox) {
			// Nested switches are already measured bottom-up. Their bounding boxes
			// represent the full subtree width, so outer lanes should honor that
			// exact width to keep sibling lanes distinct and prevent overlap.
			maxWidth = Math.max(maxWidth, bbox.width);
		} else {
			const node = nodes.find(n => n.id === nodeId);
			if (node) {
				maxWidth = Math.max(maxWidth, getNodeWidth(node));
			}
		}
	}
	return maxWidth;
}

/**
 * Gets effective height for a node, using the bounding box height if
 * the node is a nested switch with a computed bounding box.
 */
function getEffectiveNodeHeight(
	node: Node<BaseNodeData>,
	switchBoundingBoxes: Map<string, { width: number; height: number }>
): number {
	const bbox = switchBoundingBoxes.get(node.id);
	if (bbox) {
		return bbox.height;
	}
	return getNodeStackingHeight(node);
}

function getSwitchLaneMinWidth(_switchNode: Node<BaseNodeData>): number {
	// Behavior and segment branches should occupy identical lane widths.
	return SEGMENT_NODE.WIDTH;
}

function getSwitchBranchMargin(_switchNode: Node<BaseNodeData>): number {
	// Keep branch spread identical between behavior and segment switches.
	return BRANCH_MARGIN;
}

function getSwitchBranchVerticalGap(switchNode: Node<BaseNodeData>): number {
	const stepType = (switchNode.data as { automationStep?: { _type?: string } })?.automationStep?._type;
	if (stepType === 'BehaviorSwitchAutomationStep') {
		return BEHAVIOR_BRANCH_VERTICAL_GAP;
	}
	return BRANCH_VERTICAL_GAP;
}

function getSwitchNestedLaneWidthCap(_switchNode: Node<BaseNodeData>): number {
	return MAX_NESTED_LANE_WIDTH;
}

/**
 * Computes raw content extents for all branch descendant nodes in a switch.
 */
function calculateSwitchContentExtents(
	metadata: SwitchNodeMetadata,
	nodes: Node<BaseNodeData>[]
): { minX: number; maxX: number; minY: number; maxY: number } | null {
	let minX = Infinity;
	let maxX = -Infinity;
	let minY = Infinity;
	let maxY = -Infinity;

	const nodeById = new Map<string, Node<BaseNodeData>>();
	for (const n of nodes) {
		nodeById.set(n.id, n);
	}

	for (const branchNodeIds of metadata.branches.values()) {
		for (const nodeId of branchNodeIds) {
			const node = nodeById.get(nodeId);
			if (!node || !node.position) {
				continue;
			}
			const x = node.position.x;
			const y = node.position.y;
			const w = getNodeWidth(node);
			const h = getNodeHeight(node);
			minX = Math.min(minX, x);
			maxX = Math.max(maxX, x + w);
			minY = Math.min(minY, y);
			maxY = Math.max(maxY, y + h);
		}
	}

	if (minX === Infinity) {
		return null;
	}

	return { minX, maxX, minY, maxY };
}

/**
 * Computes bounding-box dimensions for a switch container.
 *
 * Width keeps visual padding to preserve lane breathing room.
 * Height intentionally uses tight content bounds (no extra container padding)
 * so nested containers do not introduce excessive vertical whitespace.
 */
function calculateSwitchBoundingBox(
	metadata: SwitchNodeMetadata,
	nodes: Node<BaseNodeData>[]
): { width: number; height: number } {
	const extents = calculateSwitchContentExtents(metadata, nodes);
	if (!extents) {
		return { width: 0, height: 0 };
	}

	return {
		width: extents.maxX - extents.minX + CONTAINER_PADDING * 2,
		height: extents.maxY - extents.minY,
	};
}

function calculateSwitchContentCenter(
	metadata: SwitchNodeMetadata,
	nodes: Node<BaseNodeData>[]
): { x: number; y: number } | null {
	const extents = calculateSwitchContentExtents(metadata, nodes);
	if (!extents) {
		return null;
	}
	return {
		x: (extents.minX + extents.maxX) / 2,
		y: (extents.minY + extents.maxY) / 2,
	};
}

/**
 * Translates all descendants of a nested switch by a given delta.
 * Used in the top-down pass to cascade position changes from outer to inner switches.
 */
function translateSwitchDescendants(
	switchNodeId: string,
	delta: { x: number; y: number },
	switchMetadata: SwitchNodeMetadata[],
	updateNodePosition: (nodeId: string, pos: { x: number; y: number }) => void,
	getNodeById: (nodeId: string) => Node<BaseNodeData> | undefined,
	visitedSwitchIds = new Set<string>(),
	/** When true, only translate this switch's direct branch nodes without
	 *  recursing into nested switches. Phase B uses this to avoid double-counting
	 *  deltas when its own loop independently handles each nesting level. */
	skipNestedRecursion = false
): void {
	if (visitedSwitchIds.has(switchNodeId)) {
		return;
	}
	visitedSwitchIds.add(switchNodeId);

	const meta = switchMetadata.find(m => m.switchNodeId === switchNodeId);
	if (!meta) {
		return;
	}

	for (const branchNodeIds of meta.branches.values()) {
		for (const nodeId of branchNodeIds) {
			const node = getNodeById(nodeId);
			if (!node) {
				continue;
			}
			updateNodePosition(nodeId, {
				x: node.position.x + delta.x,
				y: node.position.y + delta.y,
			});

			// Recurse into deeper nested switches (unless caller opted out).
			if (!skipNestedRecursion) {
				const nestedMeta = switchMetadata.find(m => m.switchNodeId === nodeId);
				if (nestedMeta && nodeId !== switchNodeId) {
					translateSwitchDescendants(
						nodeId,
						delta,
						switchMetadata,
						updateNodePosition,
						getNodeById,
						visitedSwitchIds,
						skipNestedRecursion
					);
				}
			}
		}
	}
}

/**
 * Builds an ordered chain of nodes within a branch for vertical stacking.
 * Uses edge relationships to determine the order from parent to leaf.
 *
 * @param branchNodeIds - All node IDs in the branch
 * @param edges - All edges in the flow
 * @param directChildIds - Direct children of the switch node for this branch
 * @param nestedSwitchParentById - Optional map of hidden switch id -> visual parent id
 * @returns Ordered array of node IDs from top to bottom
 */
function runGlobalContainerGraphLayout(layoutContext: LayoutContext): Map<string, { x: number; y: number }> {
	const targets = new Map<string, { x: number; y: number }>();

	const dagreGraph = new dagre.graphlib.Graph();
	dagreGraph.setDefaultEdgeLabel(() => ({}));
	dagreGraph.setGraph({
		rankdir: 'TB',
		nodesep: BRANCH_MARGIN,
		ranksep: BRANCH_VERTICAL_GAP,
		ranker: 'network-simplex',
	});

	for (const container of layoutContext.containers.values()) {
		if (container.id === layoutContext.rootContainerId) {
			continue;
		}

		dagreGraph.setNode(container.id, {
			width: Math.max(SEGMENT_NODE.WIDTH, container.localBounds.width),
			height: Math.max(STEP_NODE_COMPACT_HEIGHT, container.localBounds.height),
		});
	}

	for (const container of layoutContext.containers.values()) {
		if (!container.parentContainerId || container.parentContainerId === layoutContext.rootContainerId) {
			continue;
		}
		if (!layoutContext.containers.has(container.parentContainerId)) {
			continue;
		}
		dagreGraph.setEdge(container.parentContainerId, container.id);
	}

	if (dagreGraph.nodeCount() === 0) {
		return targets;
	}

	dagre.layout(dagreGraph);

	for (const container of layoutContext.containers.values()) {
		if (container.id === layoutContext.rootContainerId) {
			continue;
		}
		const dagreNode = dagreGraph.node(container.id);
		if (!dagreNode) {
			continue;
		}
		targets.set(container.id, {
			x: dagreNode.x,
			y: dagreNode.y,
		});
	}

	return targets;
}

function applyGlobalContainerTranslations(
	switchMetadata: SwitchNodeMetadata[],
	layoutContext: LayoutContext,
	updatedNodes: Node<BaseNodeData>[],
	updateNodePosition: (nodeId: string, pos: { x: number; y: number }) => void,
	getNodeById: (nodeId: string) => Node<BaseNodeData> | undefined
): void {
	const targetCenters = runGlobalContainerGraphLayout(layoutContext);
	if (targetCenters.size === 0) {
		return;
	}

	for (const metadata of switchMetadata) {
		const containerId = layoutContext.switchToContainerId.get(metadata.switchNodeId);
		if (!containerId) {
			continue;
		}
		const container = layoutContext.containers.get(containerId);
		if (!container) {
			continue;
		}

		// Keep top-level containers anchored to their visual parent/trunk node.
		if (container.parentContainerId === layoutContext.rootContainerId) {
			continue;
		}

		const currentCenter = calculateSwitchContentCenter(metadata, updatedNodes);
		const targetCenter = targetCenters.get(containerId);
		if (!currentCenter || !targetCenter) {
			continue;
		}

		// Preserve local horizontal lane alignment and only apply global Y translation.
		// Horizontal branch ordering is already deterministically handled in local passes.
		//
		// Global container Dagre is used as a compaction hint only: it may pull nested
		// containers upward, but should not push them farther down (which causes large
		// whitespace bands in deeply nested graphs).
		const rawDeltaY = targetCenter.y - currentCenter.y;
		if (rawDeltaY >= 0) {
			continue;
		}

		const dampedDeltaY = rawDeltaY * 0.25;
		const maxUpwardDelta = BRANCH_VERTICAL_GAP + INTRA_BRANCH_VERTICAL_GAP;
		const deltaY = Math.max(-maxUpwardDelta, dampedDeltaY);
		if (Math.abs(deltaY) < 1) {
			continue;
		}

		translateSwitchDescendants(
			metadata.switchNodeId,
			{ x: 0, y: deltaY },
			switchMetadata,
			updateNodePosition,
			getNodeById
		);
	}
}

function buildOrderedBranchChain(
	branchNodeIds: string[],
	edges: Edge[],
	directChildIds: string[],
	nestedSwitchParentById?: Map<string, string>
): string[] {
	if (branchNodeIds.length === 0) {
		return [];
	}

	// Build adjacency map for nodes within this branch
	const childMap = new Map<string, string[]>();
	const branchNodeSet = new Set(branchNodeIds);

	const addChild = (sourceId: string, targetId: string) => {
		const children = childMap.get(sourceId) || [];
		if (!children.includes(targetId)) {
			children.push(targetId);
			childMap.set(sourceId, children);
		}
	};

	for (const edge of edges) {
		if (branchNodeSet.has(edge.source) && branchNodeSet.has(edge.target)) {
			addChild(edge.source, edge.target);
		}
	}

	// Add synthetic parent->child links for nested switches that branch from a visual
	// parent node but may not have an explicit edge to the hidden switch node.
	if (nestedSwitchParentById) {
		for (const nodeId of branchNodeIds) {
			const parentId = nestedSwitchParentById.get(nodeId);
			if (parentId && branchNodeSet.has(parentId)) {
				addChild(parentId, nodeId);
			}
		}
	}

	// BFS from direct children to build ordered list
	const ordered: string[] = [];
	const visited = new Set<string>();
	const queue: string[] = [...directChildIds];

	while (queue.length > 0) {
		const nodeId = queue.shift()!;
		if (visited.has(nodeId)) {
			continue;
		}
		visited.add(nodeId);
		ordered.push(nodeId);

		// Add children to queue (maintains topological order)
		const children = childMap.get(nodeId) || [];
		for (const childId of children) {
			if (!visited.has(childId)) {
				queue.push(childId);
			}
		}
	}

	// Add any remaining nodes that weren't reachable from direct children
	// (shouldn't happen in well-formed flows, but defensive coding)
	for (const nodeId of branchNodeIds) {
		if (!visited.has(nodeId)) {
			ordered.push(nodeId);
		}
	}

	return ordered;
}

/**
 * Applies lane-based layout for switch node branches in vertical layout.
 * Each branch gets its own horizontal lane (column), positioned left-to-right
 * based on the handle index order (handle 0 = leftmost, handle N = rightmost).
 *
 * This function runs after Dagre layout and repositions branch nodes to:
 * 1. Ensure left-to-right ordering matches case index order
 * 2. Center branches around the parent switch node
 * 3. Maintain consistent vertical spacing for clean edge paths
 *
 * @param nodes - All nodes in the flow
 * @param edges - All edges in the flow
 * @returns Nodes with updated positions for lane-based layout
 */
interface SwitchBranchLayoutOptions {
	useContainerLayoutV2?: boolean;
}

export function applySwitchBranchLayout(
	nodes: Node<BaseNodeData>[],
	edges: Edge[],
	options: SwitchBranchLayoutOptions = {}
): Node<BaseNodeData>[] {
	// Defensive check: ensure inputs are valid
	if (!nodes || nodes.length === 0) {
		return nodes;
	}

	if (!edges || edges.length === 0) {
		return nodes;
	}

	const switchMetadata = identifySwitchBranches(nodes, edges);

	if (switchMetadata.length === 0) {
		return nodes;
	}

	const updatedNodes = [...nodes];
	const nodeIndexMap = new Map<string, number>();
	updatedNodes.forEach((node, index) => {
		nodeIndexMap.set(node.id, index);
	});
	const positionedNodes = new Set<string>();

	const getNodeById = (nodeId: string) => {
		const nodeIndex = nodeIndexMap.get(nodeId);
		if (nodeIndex === undefined) {
			return undefined;
		}
		return updatedNodes[nodeIndex];
	};

	const updateNodePosition = (nodeId: string, newPosition: { x: number; y: number }) => {
		const nodeIndex = nodeIndexMap.get(nodeId);
		if (nodeIndex === undefined) {
			return;
		}
		const node = updatedNodes[nodeIndex];
		updatedNodes[nodeIndex] = {
			...node,
			position: newPosition,
		};
	};

	// Build nesting tree so we know parent-child relationships between switches.
	buildSwitchNestingTree(switchMetadata);

	const switchDepthById = new Map<string, number>();
	for (const meta of switchMetadata) {
		switchDepthById.set(meta.switchNodeId, meta.nestingDepth);
	}

	const nestedSwitchParentById = new Map<string, string>();
	for (const meta of switchMetadata) {
		if (meta.visualParentId) {
			nestedSwitchParentById.set(meta.switchNodeId, meta.visualParentId);
		}
	}

	// Phase A — Bottom-up local layout (innermost switches first).
	// By processing innermost switches first, their bounding boxes are known
	// when outer switches calculate per-lane widths.
	const sortedByDepthDesc = [...switchMetadata].sort((a, b) => b.nestingDepth - a.nestingDepth);
	const switchBoundingBoxes = new Map<string, { width: number; height: number }>();

	// Snapshot ALL node positions before any Phase A repositioning so that
	// Phase B delta computation uses a consistent baseline. Recording positions
	// inside the per-switch loop caused inner switch nodes to capture already-
	// repositioned coordinates, making the subsequent delta incomplete.
	const previousPositions = new Map<string, { x: number; y: number }>();
	for (const node of updatedNodes) {
		if (node.position && typeof node.position.x === 'number' && typeof node.position.y === 'number') {
			previousPositions.set(node.id, { x: node.position.x, y: node.position.y });
		}
	}

	for (const metadata of sortedByDepthDesc) {
		const switchNodeIndex = updatedNodes.findIndex(n => n.id === metadata.switchNodeId);
		if (switchNodeIndex === -1) {
			continue;
		}

		const switchNode = updatedNodes[switchNodeIndex];

		// Defensive check: ensure switch node has valid position
		if (
			!switchNode.position ||
			typeof switchNode.position.x !== 'number' ||
			typeof switchNode.position.y !== 'number'
		) {
			continue;
		}

		// Get branch count from metadata (identified by edge caseIndex)
		const branchCount = metadata.branches.size;
		if (branchCount === 0) {
			continue;
		}

		updatedNodes[switchNodeIndex] = {
			...switchNode,
			sourcePosition: Position.Bottom,
			data: {
				...switchNode.data,
				sourcePosition: Position.Bottom,
			},
		};

		// Find the visual parent node (the step that branches connect FROM).
		// The switch node is hidden, so we need to position relative to the actual visual parent.
		const branchEdge = edges.find(e => {
			const data = e.data as { switchNodeId?: string };
			return data?.switchNodeId === metadata.switchNodeId;
		});
		const visualParentId = branchEdge?.source;
		const visualParentNode = visualParentId ? getNodeById(visualParentId) : null;

		// Use visual parent for positioning, fall back to switch node if not found
		const parentForPositioning = visualParentNode || switchNode;
		const parentHeight = getNodeHeight(parentForPositioning);
		const parentWidth = getNodeWidth(parentForPositioning);
		const parentCenterX = parentForPositioning.position.x + parentWidth / 2;

		// Get sorted branch keys for left-to-right ordering
		const sortedBranchKeys = Array.from(metadata.branches.keys()).sort((a, b) => {
			const aIndex = parseInt(a.replace('branch-', ''), 10) || 0;
			const bIndex = parseInt(b.replace('branch-', ''), 10) || 0;
			return aIndex - bIndex;
		});

		// Calculate per-lane widths using getEffectiveBranchWidth.
		// This consults switchBoundingBoxes so that nested switch widths widen the outer lane.
		const minLaneWidth = getSwitchLaneMinWidth(switchNode);
		const switchBranchMargin = getSwitchBranchMargin(switchNode);
		const maxNestedLaneWidth = getSwitchNestedLaneWidthCap(switchNode);
		const laneWidths = sortedBranchKeys.map(key => {
			const ids = metadata.branches.get(key) || [];
			return getEffectiveBranchWidth(
				ids,
				updatedNodes,
				switchBoundingBoxes,
				minLaneWidth,
				maxNestedLaneWidth,
				switchDepthById
			);
		});

		// Calculate total width and per-lane center X positions (variable width lanes).
		const totalWidth = laneWidths.reduce(
			(sum, w, i) => sum + w + (i < laneWidths.length - 1 ? switchBranchMargin : 0),
			0
		);
		const startX = parentCenterX - totalWidth / 2;

		const laneCenters: number[] = [];
		let curX = startX;
		for (const w of laneWidths) {
			laneCenters.push(curX + w / 2);
			curX += w + switchBranchMargin;
		}

		// Target Y for first branch nodes: consistent gap below visual parent (not hidden switch).
		// Behavior switches use BEHAVIOR_BRANCH_VERTICAL_GAP (120px) for extra clearance
		// between the parent step card and the case panels below it.
		const branchVerticalGap = getSwitchBranchVerticalGap(switchNode);
		const targetBranchY = parentForPositioning.position.y + parentHeight + branchVerticalGap;

		const branchLanes: { laneCenterX: number; orderedNodeIds: string[] }[] = [];

		// Build ordered node chains for each branch in caseIndex order (left to right)
		for (let branchIndex = 0; branchIndex < sortedBranchKeys.length; branchIndex++) {
			const branchKey = sortedBranchKeys[branchIndex];
			const branchNodeIds = metadata.branches.get(branchKey);

			if (!branchNodeIds || branchNodeIds.length === 0) {
				continue;
			}

			const laneCenterX = laneCenters[branchIndex];

			// First node in the branch is the direct branch edge target.
			// Fall back to the first collected node for defensive compatibility.
			const directChildId = metadata.branchRoots.get(branchKey) || branchNodeIds[0];
			const directChildIds = directChildId ? [directChildId] : [];

			// Build ordered chain of nodes for vertical stacking
			const orderedNodeIds = buildOrderedBranchChain(branchNodeIds, edges, directChildIds, nestedSwitchParentById);

			branchLanes.push({ laneCenterX, orderedNodeIds });
		}

		// Per-lane vertical stacking. Each lane computes its own Y positions
		// based on the actual heights of the nodes it contains, rather than using
		// a global row-height grid. This prevents a tall node in one lane (e.g. a
		// nested switch bounding box) from inflating whitespace in sibling lanes
		// that only contain compact nodes at the same depth.
		for (const lane of branchLanes) {
			let laneCurrentY = targetBranchY;

			for (let depth = 0; depth < lane.orderedNodeIds.length; depth++) {
				const nodeId = lane.orderedNodeIds[depth];
				if (positionedNodes.has(nodeId)) {
					// If already positioned (shared node), advance Y past it.
					const existingNode = getNodeById(nodeId);
					if (existingNode) {
						const existingHeight = getEffectiveNodeHeight(existingNode, switchBoundingBoxes);
						const nextIdForGap = depth + 1 < lane.orderedNodeIds.length ? lane.orderedNodeIds[depth + 1] : null;
						const nextNodeForGap = nextIdForGap ? getNodeById(nextIdForGap) : null;
						const nextNodeForGapIsCasePanel =
							nextNodeForGap?.type === 'segment' || nextNodeForGap?.type === 'behaviorCase';
						const existingGap = nextNodeForGapIsCasePanel ? BRANCH_VERTICAL_GAP : INTRA_BRANCH_VERTICAL_GAP;
						laneCurrentY = existingNode.position.y + existingHeight + existingGap;
					}
					continue;
				}

				const branchNode = getNodeById(nodeId);
				if (!branchNode) {
					continue;
				}

				const nodeWidth = getNodeWidth(branchNode);
				const nodeX = lane.laneCenterX - nodeWidth / 2;

				updateNodePosition(nodeId, {
					x: nodeX,
					y: laneCurrentY,
				});

				positionedNodes.add(nodeId);

				const nodeHeight = getEffectiveNodeHeight(branchNode, switchBoundingBoxes);

				// Use a larger gap before case panels (segment or behaviorCase).
				// When a step node (with its badge and schedule subtitle) transitions
				// to a case panel, the standard 52px gap is too tight.
				const nextNodeId = depth + 1 < lane.orderedNodeIds.length ? lane.orderedNodeIds[depth + 1] : null;
				const nextNode = nextNodeId ? getNodeById(nextNodeId) : null;
				const nextIsCasePanel = nextNode?.type === 'segment' || nextNode?.type === 'behaviorCase';
				const gapAfter = nextIsCasePanel ? BRANCH_VERTICAL_GAP : INTRA_BRANCH_VERTICAL_GAP;
				laneCurrentY += nodeHeight + gapAfter;
			}
		}

		// Reposition phantom target nodes for this switch.
		// Phantom targets should be positioned to the right of the default segment,
		// matching the non-nested layout behavior.
		const phantomTargetId = `phantom-target-${metadata.switchNodeId}`;
		const phantomTargetIndex = nodeIndexMap.get(phantomTargetId);
		if (phantomTargetIndex !== undefined) {
			const switchBranchEdges = edges.filter(edge => {
				if (edge.type !== 'branch') {
					return false;
				}
				const edgeData = edge.data as { switchNodeId?: string } | undefined;
				return edgeData?.switchNodeId === metadata.switchNodeId || edge.source === metadata.switchNodeId;
			});

			const defaultBranchEdge = switchBranchEdges.find(edge => {
				const edgeData = edge.data as { isDefault?: boolean } | undefined;
				return edgeData?.isDefault === true;
			});

			const lastBranchKey = sortedBranchKeys[sortedBranchKeys.length - 1];
			const fallbackRootId = lastBranchKey ? metadata.branchRoots.get(lastBranchKey) : undefined;
			const fallbackBranchNodeIds = lastBranchKey ? metadata.branches.get(lastBranchKey) : undefined;
			const anchorNodeId = defaultBranchEdge?.target || fallbackRootId || fallbackBranchNodeIds?.[0];
			const anchorNode = anchorNodeId ? getNodeById(anchorNodeId) : undefined;

			if (anchorNode) {
				const phantomX = anchorNode.position.x + SEGMENT_NODE.WIDTH + switchBranchMargin;
				updateNodePosition(phantomTargetId, {
					x: phantomX,
					y: anchorNode.position.y,
				});
			}
		}

		// Calculate and store bounding box for this switch so outer switches can use it.
		metadata.boundingBox = calculateSwitchBoundingBox(metadata, updatedNodes);
		switchBoundingBoxes.set(metadata.switchNodeId, metadata.boundingBox);

		// Re-center inner switch descendants within the allocated outer lane.
		// When getEffectiveBranchWidth clamps an inner bounding box to the
		// maxNestedLaneWidth cap, the inner branches may extend beyond the
		// outer lane allocation. Shift all inner descendants so their center
		// aligns with the outer lane center that contains them.
		if (metadata.parentSwitchId) {
			const parentMeta = switchMetadata.find(m => m.switchNodeId === metadata.parentSwitchId);
			if (parentMeta) {
				// Find which outer branch contains this switch's visual parent.
				for (const [, outerBranchNodeIds] of parentMeta.branches.entries()) {
					if (
						!outerBranchNodeIds.includes(metadata.switchNodeId) &&
						!(metadata.visualParentId && outerBranchNodeIds.includes(metadata.visualParentId))
					) {
						continue;
					}

					// The outer lane center for this branch has not been computed yet
					// (the outer switch processes later in bottom-up order). However,
					// the visual parent position IS known and the inner switch should
					// be centered on the visual parent's center X.
					if (metadata.visualParentId) {
						const vParent = getNodeById(metadata.visualParentId);
						if (vParent && metadata.boundingBox && metadata.boundingBox.width > 0) {
							const vParentCenterX = vParent.position.x + getNodeWidth(vParent) / 2;
							const bboxActual = calculateSwitchBoundingBox(metadata, updatedNodes);
							const bboxCenterX =
								bboxActual.width > 0
									? (() => {
											let minXInner = Infinity;
											let maxXInner = -Infinity;
											for (const branchIds of metadata.branches.values()) {
												for (const nid of branchIds) {
													const nd = getNodeById(nid);
													if (nd) {
														minXInner = Math.min(minXInner, nd.position.x);
														maxXInner = Math.max(maxXInner, nd.position.x + getNodeWidth(nd));
													}
												}
											}
											return minXInner === Infinity ? vParentCenterX : (minXInner + maxXInner) / 2;
										})()
									: vParentCenterX;

							const recenterDeltaX = vParentCenterX - bboxCenterX;
							if (Math.abs(recenterDeltaX) > 1) {
								for (const branchIds of metadata.branches.values()) {
									for (const nid of branchIds) {
										const nd = getNodeById(nid);
										if (nd) {
											updateNodePosition(nid, {
												x: nd.position.x + recenterDeltaX,
												y: nd.position.y,
											});
										}
									}
								}
								// Refresh bounding box after re-centering.
								metadata.boundingBox = calculateSwitchBoundingBox(metadata, updatedNodes);
								switchBoundingBoxes.set(metadata.switchNodeId, metadata.boundingBox);
							}
						}
					}
					break;
				}
			}
		}
	}

	// Phase B — Top-down coordinate translation (outermost switches first).
	// Inner switch branches were positioned relative to their visual parent's position
	// at the time they were processed. If the visual parent has since been repositioned
	// by an outer switch, cascade that delta to all inner descendants.
	const sortedByDepthAsc = [...switchMetadata].sort((a, b) => a.nestingDepth - b.nestingDepth);

	for (const metadata of sortedByDepthAsc) {
		for (const branchNodeIds of metadata.branches.values()) {
			for (const nodeId of branchNodeIds) {
				// Check if this branch node is a nested switch.
				const nestedMeta = switchMetadata.find(m => m.switchNodeId === nodeId);
				if (!nestedMeta) {
					continue;
				}

				// Find the visual parent of the nested switch.
				// Prefer metadata captured during switch discovery, fall back to edge scan.
				const nestedBranchEdge = edges.find(e => {
					const data = e.data as { switchNodeId?: string };
					return data?.switchNodeId === nestedMeta.switchNodeId;
				});
				const visualParentId = nestedMeta.visualParentId || nestedBranchEdge?.source;
				if (!visualParentId) {
					continue;
				}

				const visualParent = getNodeById(visualParentId);
				const visualParentPrevPos = previousPositions.get(visualParentId);
				if (!visualParent || !visualParentPrevPos) {
					continue;
				}

				const deltaX = visualParent.position.x - visualParentPrevPos.x;
				const deltaY = visualParent.position.y - visualParentPrevPos.y;

				if (deltaX !== 0 || deltaY !== 0) {
					// Use skipNestedRecursion so that only the immediate switch's branch
					// nodes are translated. Phase B's loop handles each nesting level
					// independently — recursing here would double-count the outer delta
					// on deeply nested descendants.
					translateSwitchDescendants(
						nodeId,
						{ x: deltaX, y: deltaY },
						switchMetadata,
						updateNodePosition,
						getNodeById,
						new Set(),
						true
					);
				}
			}
		}
	}

	// Phase B.25 — Global container graph pass (optional, enabled by default).
	// Treat switch containers as graph nodes and run a global Dagre pass so deeply
	// nested branches can distribute vertical space more consistently.
	if (options.useContainerLayoutV2 !== false) {
		const layoutContext = buildLayoutContext(switchMetadata);
		for (const metadata of switchMetadata) {
			const containerId = layoutContext.switchToContainerId.get(metadata.switchNodeId);
			if (!containerId) {
				continue;
			}
			const container = layoutContext.containers.get(containerId);
			if (!container) {
				continue;
			}
			container.localBounds = metadata.boundingBox || {
				width: SEGMENT_NODE.WIDTH,
				height: STEP_NODE_COMPACT_HEIGHT,
			};
			container.center = calculateSwitchContentCenter(metadata, updatedNodes) || {
				x: 0,
				y: 0,
			};
		}

		applyGlobalContainerTranslations(switchMetadata, layoutContext, updatedNodes, updateNodePosition, getNodeById);
	}

	// Phase B.5 — Refresh bounding boxes after coordinate translation.
	// Phase B/B.25 may have shifted inner switch descendants, making the bounding
	// boxes computed during Phase A stale. Recompute so collision detection in
	// Phase C operates on accurate geometry.
	for (const metadata of switchMetadata) {
		metadata.boundingBox = calculateSwitchBoundingBox(metadata, updatedNodes);
		switchBoundingBoxes.set(metadata.switchNodeId, metadata.boundingBox);
	}

	// Phase C — Collision detection.
	// Keep hidden nodes and phantom targets excluded, but allow visible branch nodes
	// to participate so overlaps with neighboring chains can be resolved.
	const excludeFromCollision = new Set<string>();
	for (const node of updatedNodes) {
		if (node.hidden || node.type === 'phantomTarget') {
			excludeFromCollision.add(node.id);
		}
	}

	// Detect and resolve any remaining collisions.
	// Pass edges to skip collision detection between directly connected nodes.
	// Branch-positioned nodes are anchored: they were placed by the lane
	// algorithm and should not be displaced. Other nodes move around them.
	const finalNodes = detectAndResolveCollisions(updatedNodes, edges, excludeFromCollision, positionedNodes);

	// Final pass: always anchor phantom add-path targets to the default branch root.
	// Collision resolution and nested parent translations can move segment nodes after
	// Phase A; recomputing here guarantees stable placement in the rendered canvas.
	const finalNodesWithAnchoredPhantoms = repositionPhantomTargets(finalNodes, edges, switchMetadata);

	return assignGlobalSegmentOrdinals(finalNodesWithAnchoredPhantoms);
}

function repositionPhantomTargets(
	nodes: Node<BaseNodeData>[],
	edges: Edge[],
	switchMetadata: SwitchNodeMetadata[]
): Node<BaseNodeData>[] {
	const updatedNodes = [...nodes];
	const nodeIndexById = new Map<string, number>();
	for (let index = 0; index < updatedNodes.length; index++) {
		nodeIndexById.set(updatedNodes[index].id, index);
	}

	const getNodeById = (nodeId: string) => {
		const index = nodeIndexById.get(nodeId);
		return typeof index === 'number' ? updatedNodes[index] : undefined;
	};

	const updateNodePosition = (nodeId: string, position: { x: number; y: number }) => {
		const index = nodeIndexById.get(nodeId);
		if (index === undefined) {
			return;
		}
		const node = updatedNodes[index];
		updatedNodes[index] = {
			...node,
			position,
		};
	};

	const phantomEdgeBySwitchId = new Map<string, Edge>();
	const branchRootsBySwitchId = new Map<string, Set<string>>();
	const switchIds = new Set<string>();

	for (const metadata of switchMetadata) {
		switchIds.add(metadata.switchNodeId);
		if (!branchRootsBySwitchId.has(metadata.switchNodeId)) {
			branchRootsBySwitchId.set(metadata.switchNodeId, new Set());
		}
		const rootSet = branchRootsBySwitchId.get(metadata.switchNodeId)!;
		for (const rootId of metadata.branchRoots.values()) {
			rootSet.add(rootId);
		}
	}

	for (const edge of edges) {
		if (edge.type !== 'phantomAddPath') {
			continue;
		}
		const switchNodeId = (edge.data as { switchNodeId?: unknown } | undefined)?.switchNodeId;
		if (typeof switchNodeId !== 'string' || !switchNodeId) {
			continue;
		}
		switchIds.add(switchNodeId);
		if (!phantomEdgeBySwitchId.has(switchNodeId)) {
			phantomEdgeBySwitchId.set(switchNodeId, edge);
		}
	}

	for (const node of updatedNodes) {
		if (node.type !== 'phantomTarget') {
			continue;
		}
		const match = node.id.match(/^phantom-target-(.+)$/);
		if (match?.[1]) {
			switchIds.add(match[1]);
		}
	}

	if (switchIds.size === 0) {
		return nodes;
	}

	for (const switchNodeId of switchIds) {
		const phantomTargetFromEdge = phantomEdgeBySwitchId.get(switchNodeId)?.target;
		const phantomTargetId =
			typeof phantomTargetFromEdge === 'string' ? phantomTargetFromEdge : `phantom-target-${switchNodeId}`;
		if (!nodeIndexById.has(phantomTargetId)) {
			continue;
		}

		const switchNode = getNodeById(switchNodeId);
		const switchBranchMargin = switchNode ? getSwitchBranchMargin(switchNode) : BRANCH_MARGIN;

		const switchBranchEdges = edges.filter(edge => {
			if (edge.type !== 'branch') {
				return false;
			}
			const edgeData = edge.data as { switchNodeId?: string } | undefined;
			if (edgeData?.switchNodeId === switchNodeId || edge.source === switchNodeId) {
				const rootSet = branchRootsBySwitchId.get(switchNodeId) ?? new Set<string>();
				rootSet.add(edge.target);
				branchRootsBySwitchId.set(switchNodeId, rootSet);
				return true;
			}
			return false;
		});

		const defaultBranchEdge = switchBranchEdges.find(edge => {
			const edgeData = edge.data as { isDefault?: boolean } | undefined;
			return edgeData?.isDefault === true;
		});

		let anchorNodeId: string | undefined = defaultBranchEdge?.target;

		const segmentRoots = updatedNodes.filter(node => {
			if (node.type !== 'segment') {
				return false;
			}
			const parentSwitchNodeId = (node.data as { parentSwitchNodeId?: unknown } | undefined)?.parentSwitchNodeId;
			return parentSwitchNodeId === switchNodeId;
		});

		// Fallback 1: resolve default via segment node metadata when branch edge metadata omits isDefault.
		if (!anchorNodeId && segmentRoots.length > 0) {
			const defaultSegment = segmentRoots.find(
				node => (node.data as { isDefault?: unknown } | undefined)?.isDefault === true
			);
			if (defaultSegment) {
				anchorNodeId = defaultSegment.id;
			}
		}

		// Fallback 2: choose the highest caseIndex branch (commonly the default slot in legacy data).
		if (!anchorNodeId && switchBranchEdges.length > 0) {
			const byCaseIndex = [...switchBranchEdges]
				.map(edge => {
					const raw = (edge.data as { caseIndex?: unknown } | undefined)?.caseIndex;
					const caseIndex = typeof raw === 'number' && Number.isFinite(raw) ? raw : -Infinity;
					return { edge, caseIndex };
				})
				.sort((a, b) => a.caseIndex - b.caseIndex);
			const lastByIndex = byCaseIndex[byCaseIndex.length - 1];
			if (lastByIndex && Number.isFinite(lastByIndex.caseIndex)) {
				anchorNodeId = lastByIndex.edge.target;
			}
		}

		// Fallback 3: use highest segmentIndex among branch roots for this switch.
		if (!anchorNodeId && segmentRoots.length > 0) {
			const bySegmentIndex = [...segmentRoots]
				.map(node => {
					const raw = (node.data as { segmentIndex?: unknown } | undefined)?.segmentIndex;
					const segmentIndex = typeof raw === 'number' && Number.isFinite(raw) ? raw : -Infinity;
					return { node, segmentIndex };
				})
				.sort((a, b) => a.segmentIndex - b.segmentIndex);
			const lastByIndex = bySegmentIndex[bySegmentIndex.length - 1];
			if (lastByIndex && Number.isFinite(lastByIndex.segmentIndex)) {
				anchorNodeId = lastByIndex.node.id;
			}
		}

		// Last-resort fallback: keep target aligned to the right-most known branch root.
		if (!anchorNodeId) {
			let rightMostRootId: string | undefined;
			let rightMostX = -Infinity;
			for (const branchRootId of branchRootsBySwitchId.get(switchNodeId) ?? []) {
				const branchRootNode = getNodeById(branchRootId);
				if (!branchRootNode || !branchRootNode.position) {
					continue;
				}
				if (branchRootNode.position.x > rightMostX) {
					rightMostX = branchRootNode.position.x;
					rightMostRootId = branchRootId;
				}
			}
			anchorNodeId = rightMostRootId;
		}

		if (!anchorNodeId) {
			continue;
		}

		const anchorNode = getNodeById(anchorNodeId);
		if (!anchorNode || !anchorNode.position) {
			continue;
		}

		const anchorWidth = getNodeWidth(anchorNode);
		updateNodePosition(phantomTargetId, {
			x: anchorNode.position.x + anchorWidth + switchBranchMargin,
			y: anchorNode.position.y,
		});
	}

	return updatedNodes;
}

function getBranchCaseKey(data: Record<string, unknown>): string | null {
	const parentSwitchNodeId = typeof data.parentSwitchNodeId === 'string' ? data.parentSwitchNodeId : null;
	const caseId = typeof data.caseId === 'string' ? data.caseId : null;
	if (!parentSwitchNodeId || !caseId) {
		return null;
	}
	return `${parentSwitchNodeId}::${caseId}`;
}

function assignGlobalSegmentOrdinals(nodes: Node<BaseNodeData>[]): Node<BaseNodeData>[] {
	const branchEntryNodes = nodes.filter(node => node.type === 'segment' || node.type === 'behaviorCase');
	if (branchEntryNodes.length === 0) {
		return nodes;
	}

	const sortedBranchEntries = [...branchEntryNodes].sort((a, b) => {
		if (a.position.y !== b.position.y) {
			return a.position.y - b.position.y;
		}
		if (a.position.x !== b.position.x) {
			return a.position.x - b.position.x;
		}
		const pathA =
			typeof (a.data as { segmentPathKey?: unknown }).segmentPathKey === 'string'
				? ((a.data as { segmentPathKey?: string }).segmentPathKey ?? '')
				: '';
		const pathB =
			typeof (b.data as { segmentPathKey?: unknown }).segmentPathKey === 'string'
				? ((b.data as { segmentPathKey?: string }).segmentPathKey ?? '')
				: '';
		if (pathA !== pathB) {
			return pathA.localeCompare(pathB);
		}
		return a.id.localeCompare(b.id);
	});

	const caseInfoByKey = new Map<string, { ordinal: number; caseName: string; segmentPathKey: string }>();
	const nextById = new Map<string, Node<BaseNodeData>>();

	for (let index = 0; index < sortedBranchEntries.length; index++) {
		const node = sortedBranchEntries[index];
		const ordinal = index + 1;
		const existingPathKey =
			typeof (node.data as { segmentPathKey?: unknown }).segmentPathKey === 'string'
				? ((node.data as { segmentPathKey?: string }).segmentPathKey ?? undefined)
				: undefined;
		const fallbackPathKey = `${node.type}:${node.id}`;
		const segmentPathKey = existingPathKey || fallbackPathKey;
		const caseKey = getBranchCaseKey(node.data as Record<string, unknown>);

		if (node.type === 'segment') {
			const isDefault = (node.data as { isDefault?: unknown }).isDefault === true;
			const explicitSegmentName =
				typeof (node.data as { segmentName?: unknown }).segmentName === 'string'
					? ((node.data as { segmentName?: string }).segmentName ?? '')
					: '';
			const segmentCaseName = explicitSegmentName || (isDefault ? 'Default' : `Segment #${ordinal}`);
			if (caseKey) {
				caseInfoByKey.set(caseKey, { ordinal, caseName: segmentCaseName, segmentPathKey });
			}

			nextById.set(node.id, {
				...node,
				data: {
					...node.data,
					label: isDefault ? node.data.label : `Segment #${ordinal}`,
					globalSegmentOrdinal: ordinal,
					segmentPathKey,
				},
			});
			continue;
		}

		const branchCaseName =
			typeof (node.data as { caseName?: unknown }).caseName === 'string'
				? ((node.data as { caseName?: string }).caseName ?? node.data.label)
				: node.data.label;
		if (caseKey) {
			caseInfoByKey.set(caseKey, { ordinal, caseName: branchCaseName, segmentPathKey });
		}
		nextById.set(node.id, {
			...node,
			data: {
				...node.data,
				globalSegmentOrdinal: ordinal,
				segmentPathKey,
			},
		});
	}

	for (const node of nodes) {
		if (nextById.has(node.id)) {
			continue;
		}
		const parentCaseInfo = (node.data as { parentCaseInfo?: unknown }).parentCaseInfo;
		if (!parentCaseInfo || typeof parentCaseInfo !== 'object') {
			continue;
		}
		const parentCaseRecord = parentCaseInfo as Record<string, unknown>;
		const parentSwitchNodeId =
			typeof parentCaseRecord.parentSwitchNodeId === 'string' ? parentCaseRecord.parentSwitchNodeId : null;
		const caseId = typeof parentCaseRecord.caseId === 'string' ? parentCaseRecord.caseId : null;
		if (!parentSwitchNodeId || !caseId) {
			continue;
		}
		const caseInfo = caseInfoByKey.get(`${parentSwitchNodeId}::${caseId}`);
		if (!caseInfo) {
			continue;
		}
		nextById.set(node.id, {
			...node,
			data: {
				...node.data,
				parentCaseInfo: {
					...parentCaseRecord,
					caseName: caseInfo.caseName,
					globalSegmentOrdinal: caseInfo.ordinal,
					segmentPathKey: caseInfo.segmentPathKey,
				},
			},
		});
	}

	return nodes.map(node => nextById.get(node.id) || node);
}

/**
 * Represents a bounding box for collision detection
 */
interface BoundingBox {
	left: number;
	right: number;
	top: number;
	bottom: number;
}

/**
 * Gets the bounding box for a node
 * Returns null if node has invalid position
 */
function getNodeBoundingBox(node: Node<BaseNodeData>): BoundingBox | null {
	// Handle nodes with invalid positions
	if (!node.position || typeof node.position.x !== 'number' || typeof node.position.y !== 'number') {
		return null;
	}

	const width = getNodeWidth(node);
	const height = getNodeHeight(node);
	return {
		left: node.position.x,
		right: node.position.x + width,
		top: node.position.y,
		bottom: node.position.y + height,
	};
}

/**
 * Checks if two bounding boxes overlap
 */
function boxesOverlap(a: BoundingBox, b: BoundingBox): boolean {
	return a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;
}

/**
 * Checks if two nodes are directly connected by an edge
 */
function areNodesConnected(nodeIdA: string, nodeIdB: string, connectedPairs: Set<string>): boolean {
	return connectedPairs.has(`${nodeIdA}-${nodeIdB}`) || connectedPairs.has(`${nodeIdB}-${nodeIdA}`);
}

/**
 * Detects overlapping nodes and adjusts positions to resolve collisions.
 *
 * Strategy:
 * - If two overlapping nodes share the same Y band (horizontally adjacent
 *   branches), push the rightmost node further right to create a gap.
 * - If two overlapping nodes share the same X band (vertically stacked
 *   in the same chain), push the lower node further down.
 * - Directly connected nodes are skipped because they are expected to be
 *   vertically adjacent in the same flow chain.
 *
 * @param nodes - Nodes to check for collisions
 * @param edges - Edges connecting nodes (to skip connected pairs)
 * @returns Nodes with adjusted positions to prevent overlaps
 */
function detectAndResolveCollisions(
	nodes: Node<BaseNodeData>[],
	edges: Edge[],
	excludeFromCollision?: Set<string>,
	anchoredNodeIds?: Set<string>
): Node<BaseNodeData>[] {
	if (nodes.length < 2) {
		return nodes;
	}

	// Build a set of connected node pairs for fast lookup
	const connectedPairs = new Set<string>();
	for (const edge of edges) {
		connectedPairs.add(`${edge.source}-${edge.target}`);
	}

	const updatedNodes = [...nodes];
	const collisionMargin = 40; // Minimum gap between nodes (consistent with CONTAINER_PADDING)
	let hasCollisions = true;
	let iterations = 0;
	const maxIterations = 10; // Prevent infinite loops

	while (hasCollisions && iterations < maxIterations) {
		hasCollisions = false;
		iterations++;

		for (let i = 0; i < updatedNodes.length; i++) {
			const nodeA = updatedNodes[i];

			// Skip excluded nodes (branch-positioned, hidden, phantom targets).
			// These are already correctly positioned and must not be displaced.
			if (excludeFromCollision?.has(nodeA.id)) {
				continue;
			}

			const boxA = getNodeBoundingBox(nodeA);

			// Skip nodes with invalid positions
			if (!boxA) {
				continue;
			}

			for (let j = i + 1; j < updatedNodes.length; j++) {
				const nodeB = updatedNodes[j];

				// Skip excluded nodes in inner loop as well
				if (excludeFromCollision?.has(nodeB.id)) {
					continue;
				}

				const boxB = getNodeBoundingBox(nodeB);

				// Skip nodes with invalid positions
				if (!boxB) {
					continue;
				}

				// Skip collision detection for directly connected nodes only
				// when they are in the same vertical lane. Connected nodes that
				// spread horizontally (e.g. a step branching to segment case
				// panels) can still overlap and must be checked.
				if (areNodesConnected(nodeA.id, nodeB.id, connectedPairs)) {
					const xDistance = Math.abs(boxA.left - boxB.left);
					const laneThreshold = NODE_WIDTH * 0.5; // Same lane if within half a node width
					if (xDistance < laneThreshold) {
						continue;
					}
				}

				// Check for actual overlap (bounding boxes intersect)
				if (boxesOverlap(boxA, boxB)) {
					// Branch-positioned nodes are anchored: the lane algorithm placed
					// them deliberately and they must not be displaced by the generic
					// collision resolver. When both nodes are anchored, skip entirely
					// (branch layout is authoritative). When one is anchored, only the
					// non-anchored node moves.
					const aIsAnchored = anchoredNodeIds?.has(nodeA.id) === true;
					const bIsAnchored = anchoredNodeIds?.has(nodeB.id) === true;

					if (aIsAnchored && bIsAnchored) {
						continue;
					}

					hasCollisions = true;

					// Determine the smaller axis of overlap to pick the most
					// efficient resolution direction.
					const overlapX = Math.min(boxA.right, boxB.right) - Math.max(boxA.left, boxB.left);
					const overlapY = Math.min(boxA.bottom, boxB.bottom) - Math.max(boxA.top, boxB.top);

					if (overlapX < overlapY) {
						// Resolve by pushing apart horizontally (smaller overlap axis).
						// Never move an anchored node; only the other node is displaced.
						const pushX = overlapX + collisionMargin;
						const moveB = !bIsAnchored && (aIsAnchored || boxB.left >= boxA.left);
						if (moveB) {
							updatedNodes[j] = {
								...updatedNodes[j],
								position: {
									x: updatedNodes[j].position.x + pushX,
									y: updatedNodes[j].position.y,
								},
							};
						} else {
							updatedNodes[i] = {
								...updatedNodes[i],
								position: {
									x: updatedNodes[i].position.x + pushX,
									y: updatedNodes[i].position.y,
								},
							};
						}
					} else {
						// Resolve by pushing apart vertically.
						// Never move an anchored node; only the other node is displaced.
						const pushY = overlapY + collisionMargin;
						const moveB = !bIsAnchored && (aIsAnchored || boxB.top >= boxA.top);
						if (moveB) {
							updatedNodes[j] = {
								...updatedNodes[j],
								position: {
									x: updatedNodes[j].position.x,
									y: updatedNodes[j].position.y + pushY,
								},
							};
						} else {
							updatedNodes[i] = {
								...updatedNodes[i],
								position: {
									x: updatedNodes[i].position.x,
									y: updatedNodes[i].position.y + pushY,
								},
							};
						}
					}
				}
			}
		}
	}

	if (iterations >= maxIterations) {
		console.warn('[layoutEngine] Max collision resolution iterations reached. Some overlaps may remain.');
	}

	return updatedNodes;
}
